import { Alert, Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import { getCounterpartySummary } from 'utils/helpers/invoiceSectionSummaryHelpers';

import { InvoiceVerificationCard } from '../InvoiceVerificationCard';
import { CounterpartyAutocomplete } from './CounterpartyAutocomplete';
import { CounterpartyDisplayCard } from './CounterpartyDisplayCard';
import { NewCounterpartyCard } from './NewCounterpartyCard';

interface CounterpartyCardProps {
  currentInvoice: Invoice;
  confidenceLevel?: ConfidenceLevel;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
  hasError?: boolean;
}

export const CounterpartyCard = ({
  currentInvoice,
  confidenceLevel,
  expanded,
  onToggle,
  onExpanded,
  autoExpandReason,
  hasError
}: CounterpartyCardProps) => {
  const { translate } = useTranslations();
  const { watch } = useFormContext();

  const watchedCounterpartyName = watch(
    InvoiceVerificationFieldName.CounterpartyName
  );
  const counterpartyId = useWatch({
    name: InvoiceVerificationFieldName.CounterpartyId
  });
  const counterpartyVatNumber = useWatch({
    name: InvoiceVerificationFieldName.CounterpartyVatNumber
  });
  const counterpartyRegistrationNumber = useWatch({
    name: InvoiceVerificationFieldName.CounterpartyRegistrationNumber
  });

  const summary = useMemo(
    () =>
      getCounterpartySummary(
        {
          counterpartyId,
          counterpartyName: watchedCounterpartyName,
          counterpartyVatNumber,
          counterpartyRegistrationNumber
        },
        translate
      ),
    [
      counterpartyId,
      watchedCounterpartyName,
      counterpartyVatNumber,
      counterpartyRegistrationNumber,
      translate
    ]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [initialCardName, setInitialCardName] = useState('');
  const [createdCounterpartyName, setCreatedCounterpartyName] = useState('');

  const isCounterpartySelected = Boolean(watchedCounterpartyName);
  const previousCounterpartyIdRef = useRef<number | undefined>(counterpartyId);
  const justCreatedCounterpartyRef = useRef(false);

  const resetCreateCard = () => {
    setShowCreateCard(false);
    setInitialCardName('');
  };

  useEffect(() => {
    if (
      counterpartyId !== previousCounterpartyIdRef.current &&
      counterpartyId
    ) {
      if (showCreateCard) {
        resetCreateCard();
      }
      setIsEditing(false);
      if (justCreatedCounterpartyRef.current) {
        justCreatedCounterpartyRef.current = false;
      } else {
        setCreatedCounterpartyName('');
      }
    }
    previousCounterpartyIdRef.current = counterpartyId;
  }, [counterpartyId, showCreateCard]);

  const handleCancel = () => {
    resetCreateCard();
    setIsEditing(false);
    setCreatedCounterpartyName('');
  };

  const handleCreateSuccess = (name: string) => {
    justCreatedCounterpartyRef.current = true;
    setCreatedCounterpartyName(name);
    resetCreateCard();
    setIsEditing(false);
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      resetCreateCard();
    }
    setCreatedCounterpartyName('');
    setIsEditing((prev) => !prev);
  };

  return (
    <InvoiceVerificationCard
      title={translate('labels.counterparty')}
      summary={summary}
      confidenceLevel={confidenceLevel}
      expanded={expanded}
      onToggle={onToggle}
      onExpanded={onExpanded}
      autoExpandReason={autoExpandReason}
      hasError={hasError}
    >
      <Box>
        {createdCounterpartyName && (
          <Alert
            severity="success"
            data-testid="counterparty-created-success"
            sx={{ mb: 2 }}
          >
            {translate('messages.counterpartyCreatedAndSelected', {
              name: createdCounterpartyName
            })}
          </Alert>
        )}
        {isEditing && (
          <Box sx={{ mb: 2 }}>
            <CounterpartyAutocomplete
              currentInvoice={currentInvoice}
              watchedCounterpartyName={watchedCounterpartyName}
              showCancel={!isCounterpartySelected}
              showCreateCard={showCreateCard}
              onCancel={handleCancel}
              onSearchInputChange={resetCreateCard}
              onCreateClick={(searchValue) => {
                setInitialCardName(searchValue);
                setShowCreateCard(true);
              }}
            />
          </Box>
        )}
        {showCreateCard && (
          <Box sx={{ mb: 2 }}>
            <NewCounterpartyCard
              currentInvoice={currentInvoice}
              initialName={initialCardName}
              onSuccess={handleCreateSuccess}
            />
          </Box>
        )}
        {!(isEditing && !isCounterpartySelected) && (
          <CounterpartyDisplayCard
            currentInvoice={currentInvoice}
            isEditing={isEditing}
            onToggleEdit={handleToggleEdit}
          />
        )}
      </Box>
    </InvoiceVerificationCard>
  );
};
